import { formatError } from "helpers/formatError";

const GENERIC_KEY = "_";

const pickMessage = value => {
  if (value == null) return null;
  if (Array.isArray(value)) {
    const first = value.find(v => typeof v === "string" && v.length > 0);
    return first ?? null;
  }
  if (typeof value === "string") return value;
  if (typeof value === "object" && typeof value.message === "string") {
    return value.message;
  }
  return null;
};

/**
 * Normalize a 422 row-validation error response into a uniform shape.
 *
 * Preferred contract:
 *   `{errors: {<field>: msg, _: msg}, error_details: [{field, code, message}]}`
 *   - `errors` drives cell-level highlighting.
 *   - `error_details` carries per-field codes (REQUIRED, DUPLICATE_VALUE, ...).
 *   - `_` in `errors` is the toast fallback for unparseable conflicts.
 *
 * Also accepts the legacy list shape `{errors: [{field, code, message}]}`
 * for resilience against older API responses.
 *
 * Returns `{status, error, fieldErrors, genericError}`. Each `fieldErrors`
 * entry is `{code, message}`; `code` is filled from `error_details` when
 * available.
 */
export const normalizeRowError = (err, fallback = "Something went wrong.") => {
  const status = err?.response?.status;
  const data = err?.response?.data ?? null;
  const rawErrors = data?.errors;
  const fieldErrors = {};
  let genericError = null;

  if (rawErrors && !Array.isArray(rawErrors) && typeof rawErrors === "object") {
    for (const [key, value] of Object.entries(rawErrors)) {
      const msg = pickMessage(value);
      if (!msg) continue;
      if (key === GENERIC_KEY) {
        genericError = msg;
      } else {
        fieldErrors[key] = { code: value?.code, message: msg };
      }
    }
  } else if (Array.isArray(rawErrors)) {
    for (const entry of rawErrors) {
      const msg = pickMessage(entry);
      if (!msg) continue;
      const field = entry?.field;
      if (!field || field === GENERIC_KEY) {
        if (!genericError) genericError = msg;
      } else {
        fieldErrors[field] = { code: entry?.code, message: msg };
      }
    }
  }

  // Enrich field-level codes from `error_details` when the dict-shape
  // `errors` only carried message strings.
  const details = data?.error_details;
  if (Array.isArray(details)) {
    for (const detail of details) {
      const field = detail?.field;
      if (!field || field === GENERIC_KEY) continue;
      const existing = fieldErrors[field];
      if (existing) {
        if (!existing.code && detail.code) existing.code = detail.code;
      } else if (detail.message) {
        fieldErrors[field] = { code: detail.code, message: detail.message };
      }
    }
  }

  if (Object.keys(fieldErrors).length === 0 && !genericError) {
    genericError = formatError(err, fallback);
  }

  return {
    status,
    error: data?.error ?? null,
    fieldErrors,
    genericError,
  };
};

export { pickMessage };
